import {
  Grid,
  Typography,
  Box,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect, useState } from "react";
import { useEditMode } from "../../context/EditModeContext";
import { useSupabaseTable } from "../../hooks/useSupabaseTable";
import { Add } from "@mui/icons-material";

type ProfileHeaderData = {
  id: number;
  name: string;
  subtitle: string;
  description: string;
  bullets: string[];
  photoUrl: string;
};


export default function ProfileHeader() {
  const { isEditMode } = useEditMode();

  const { data: supabaseData, update, insert } =
    useSupabaseTable<ProfileHeaderData>("profile_header", { realtime: true });

  const [editData, setEditData] = useState<ProfileHeaderData>({
    id: 0,
    name: "",
    subtitle: "",
    description: "",
    bullets: [],
    photoUrl: "",
  });

  const [originalData, setOriginalData] = useState<ProfileHeaderData | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (supabaseData && supabaseData.length > 0) {
      setEditData(supabaseData[0]);
      setOriginalData(supabaseData[0]);
    }
  }, [supabaseData]);

  const handleChange = (field: keyof ProfileHeaderData, value: string) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleBulletChange = (index: number, value: string) => {
    const newBullets = [...editData.bullets];
    newBullets[index] = value;
    setEditData((prev) => ({ ...prev, bullets: newBullets }));
    setDirty(true);
  };

  const handleAddBullet = () => {
    setEditData((prev) => ({ ...prev, bullets: [...prev.bullets, ""] }));
    setDirty(true);
  };

  const handleRemoveBullet = (index: number) => {
    const newBullets = [...editData.bullets];
    newBullets.splice(index, 1);
    setEditData((prev) => ({ ...prev, bullets: newBullets }));
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      if (editData.id && editData.id !== 0) {
        await update(editData, { id: editData.id });
      } else {
        await insert(editData);
      }
      setOriginalData(editData);
      setDirty(false);
    } catch (err) {
      console.error("Error saving profile header:", err);
    }
  };

  const handleCancel = () => {
    if (originalData) {
      setEditData(originalData);
      setDirty(false);
    }
  };

  return (
    <Paper sx={{ p: 3, backgroundColor: "#F5F5F5", borderRadius: 2, boxShadow: 2 }}>
      <Grid container spacing={4} alignItems="flex-start">
        {/* Text Section */}
        <Grid size={{ xs: 12, md: 8 }}>
          {isEditMode ? (
            <>
              <TextField
                fullWidth
                label="Name"
                value={editData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Subtitle"
                value={editData.subtitle}
                onChange={(e) => handleChange("subtitle", e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={editData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                sx={{ mb: 2 }}
              />

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Bullet Points
              </Typography>
              {editData.bullets.map((bullet, i) => (
                <Box display="flex" alignItems="center" key={i} sx={{ mb: 1 }}>
                  <TextField
                    fullWidth
                    label={`Bullet ${i + 1}`}
                    value={bullet}
                    onChange={(e) => handleBulletChange(i, e.target.value)}
                  />
                  <IconButton
                    aria-label="remove"
                    color="error"
                    onClick={() => handleRemoveBullet(i)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<Add />}
                onClick={handleAddBullet}
                variant="outlined"
                sx={{ mt: 2 }}
              >
                Add Bullet
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {editData.name}
              </Typography>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: "bold", color: "#1976D2" }}
              >
                {editData.subtitle}
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                {editData.description}
              </Typography>
              <List dense sx={{ pl: 2 }}>
                {editData.bullets.map((bullet, i) => (
                  <ListItem key={i}>
                    <ListItemIcon sx={{ minWidth: "30px" }}>
                      <CheckCircleIcon sx={{ color: "#1976D2" }} />
                    </ListItemIcon>
                    <ListItemText primary={bullet} />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Grid>

        {/* Photo Section */}
        <Grid
          size={{ xs: 12, md: 4 }}
          display="flex"
          justifyContent={{ xs: "center", md: "flex-end" }}
        >
          <Box>
            {isEditMode ? (
              <TextField
                fullWidth
                label="Photo URL"
                value={editData.photoUrl}
                onChange={(e) => handleChange("photoUrl", e.target.value)}
              />
            ) : (
              <Avatar
                src={"https://media.licdn.com/dms/image/v2/D4D03AQE6-KJKEMNxsQ/profile-displayphoto-scale_200_200/B4DZrjBrDNIgAc-/0/1764745460034?e=2147483647&v=beta&t=mcRt2Riovw_MiuPUakoMngpFydsHgIgqCgE7XiirDqM"}
                alt={editData.name}
                sx={{
                  width: { xs: 150, md: 200 },
                  height: { xs: 150, md: 200 },
                  borderRadius: "50%",
                  boxShadow: 5,
                  border: "3px solid #1976D2",
                }}
              />
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Save / Cancel buttons */}
      {
        isEditMode && dirty && (
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleSave} sx={{ mr: 2 }}>
              Save
            </Button>
            <Button variant="outlined" color="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          </Box>
        )
      }
    </Paper >
  );
}

